"use client";

/**
 * 租户套餐定价区域组件
 * 展示所有租户套餐的定价卡片
 */

import { useState, useMemo, useEffect, useRef } from "react";
import Link from "next/link";
import { useTranslations, useLocale } from "next-intl";
import { useBillingPlans, TenantPlanDetails } from "@/hooks/useBillingPlans";
import TenantPricingCard from "./TenantPricingCard";
import { TrialApplicationDialog } from "./TrialApplicationDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Building2, ArrowRight } from "lucide-react";
import { Heading, Text } from "@/components/ui/typography";
import { Button } from "@/components/ui/button";

// 获取套餐的试用申请类型
function getApplicationType(tier: string): "AUTO" | "ELIGIBILITY" | "SALES" {
  const tierLower = tier?.toLowerCase();
  if (tierLower === "team" || tierLower === "business") return "AUTO";
  if (tierLower === "education" || tierLower === "startup")
    return "ELIGIBILITY";
  return "SALES";
}

interface TenantPricingSectionProps {
  onSelectPlan?: (
    plan: TenantPlanDetails,
    seats: number,
    billingCycle: "monthly" | "yearly",
  ) => void;
  hasSubscription?: boolean;
}

export default function TenantPricingSection({
  onSelectPlan,
  hasSubscription = false,
}: TenantPricingSectionProps) {
  const t = useTranslations("billingPage");
  const locale = useLocale();
  const { grouped, loading, error } = useBillingPlans({ family: "tenant" });
  const [selectedCategory, setSelectedCategory] = useState<
    "all" | "standard" | "special"
  >("all");

  // 试用申请对话框状态
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<TenantPlanDetails | null>(
    null,
  );
  const [verifiedApplicationId, setVerifiedApplicationId] = useState<
    string | undefined
  >();

  // 租户检测状态
  const [hasTenant, setHasTenant] = useState<boolean | null>(null);
  const [checkingTenant, setCheckingTenant] = useState(true);

  // 防止重复处理邮箱验证
  const emailVerifiedHandled = useRef(false);

  // 检测用户是否有租户
  useEffect(() => {
    const checkTenant = async () => {
      try {
        const res = await fetch("/api/tenant/current");
        if (res.ok) {
          const data = await res.json();
          setHasTenant(!!data.tenantId);
        } else {
          setHasTenant(false);
        }
      } catch {
        setHasTenant(false);
      } finally {
        setCheckingTenant(false);
      }
    };
    checkTenant();
  }, []);

  // 检测 URL 参数中的邮箱验证状态，自动打开 Enterprise 试用申请对话框
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (emailVerifiedHandled.current) return;
    if (loading || !grouped?.tenant?.length) {
      console.log(
        "[TenantPricingSection] Waiting for data, loading:",
        loading,
        "hasData:",
        !!grouped?.tenant?.length,
      );
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const emailVerified = params.get("emailVerified");
    const applicationId = params.get("applicationId");
    const verifiedEmail = params.get("verifiedEmail");
    console.log(
      "[TenantPricingSection] useEffect fired, emailVerified:",
      emailVerified,
      "applicationId:",
      applicationId,
    );

    if (emailVerified === "true") {
      emailVerifiedHandled.current = true;
      if (applicationId) {
        setVerifiedApplicationId(applicationId);
      }
      console.log(
        "[TenantPricingSection] Email verified, looking for Enterprise plan",
      );
      // 找到 Enterprise 套餐
      const enterprisePlan = grouped.tenant.find(
        (p) => p.tier?.toLowerCase() === "enterprise",
      );
      console.log(
        "[TenantPricingSection] Enterprise plan found:",
        !!enterprisePlan,
        enterprisePlan?.code,
      );
      if (enterprisePlan) {
        setSelectedPlan(enterprisePlan);
        setDialogOpen(true);
        console.log(
          "[TenantPricingSection] Dialog opened for Enterprise trial",
        );
      } else {
        console.warn(
          "[TenantPricingSection] No Enterprise plan found in:",
          grouped.tenant.map((p) => p.tier),
        );
      }
      // 注意：不在这里清除 URL 参数，让 TrialApplicationDialog 处理
    }
  }, [grouped?.tenant, loading]);

  // 按类别分组套餐
  const categorizedPlans = useMemo(() => {
    if (!grouped?.tenant) return { standard: [], special: [] };

    const monthlyPlans = grouped.tenant.filter(
      (p) => p.billingCycle === "monthly",
    );

    // 标准套餐：Team, Business
    const standard = monthlyPlans.filter((p) =>
      ["team", "business"].includes(p.tier?.toLowerCase() || ""),
    );

    // 特殊套餐：Education, Startup, Enterprise
    const special = monthlyPlans.filter((p) =>
      ["education", "startup", "enterprise"].includes(
        p.tier?.toLowerCase() || "",
      ),
    );

    return { standard, special };
  }, [grouped?.tenant]);

  // 显示的套餐
  const displayPlans = useMemo(() => {
    if (selectedCategory === "standard") return categorizedPlans.standard;
    if (selectedCategory === "special") return categorizedPlans.special;
    return [...categorizedPlans.standard, ...categorizedPlans.special];
  }, [selectedCategory, categorizedPlans]);

  // 处理套餐选择
  const handleSelectPlan = (
    plan: TenantPlanDetails,
    seats: number,
    billingCycle: "monthly" | "yearly",
  ) => {
    const applicationType = getApplicationType(plan.tier || "");

    // 对于需要申请的套餐（Education/Startup/Enterprise），打开申请对话框
    if (applicationType === "ELIGIBILITY" || applicationType === "SALES") {
      setSelectedPlan(plan);
      setDialogOpen(true);
    } else {
      // 对于自动开通的套餐（Team/Business），调用外部回调进行购买流程
      onSelectPlan?.(plan, seats, billingCycle);
    }
  };

  if (loading || checkingTenant) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <Skeleton className="h-8 w-48 mx-auto mb-2" />
          <Skeleton className="h-4 w-64 mx-auto" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[500px] rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {t("loadPlansFailed")}: {error}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div id="tenant-plans" className="space-y-8">
      {/* 标题 */}
      <div className="text-center">
        <Heading variant="h2" className="tracking-tight">
          {t("tenantPlanTitle")}
        </Heading>
        <Text variant="body" muted className="mt-2">
          {t("tenantPlanSubtitle")}
        </Text>
      </div>

      {/* 无租户引导提示 */}
      {hasTenant === false && (
        <Alert className="border-blue-200 bg-blue-50 dark:bg-blue-900/20">
          <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="flex flex-col gap-3">
            <div>
              <span className="font-medium text-blue-800 dark:text-blue-200">
                {t("tenantPlan.requiresTenant") ||
                  "Team plans require a tenant"}
              </span>
              <p className="text-sm text-blue-700/80 dark:text-blue-300/80 mt-1">
                {t("tenantPlan.requiresTenantDesc") ||
                  "To subscribe to a team plan, you need to create or join a tenant first."}
              </p>
            </div>
            <div className="flex gap-2">
              <Link href={`/${locale}/tenants`} className="self-start">
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Building2 className="h-4 w-4 mr-2" />
                  {t("tenantPlan.createTenant") || "Create Tenant"}
                </Button>
              </Link>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* 分类标签 */}
      <Tabs
        value={selectedCategory}
        onValueChange={(v) => setSelectedCategory(v as any)}
        className="w-full"
      >
        <TabsList className="grid w-full max-w-md mx-auto grid-cols-3">
          <TabsTrigger value="all">{t("allPlans")}</TabsTrigger>
          <TabsTrigger value="standard">{t("standardPlans")}</TabsTrigger>
          <TabsTrigger value="special">{t("specialPlans")}</TabsTrigger>
        </TabsList>

        <TabsContent value={selectedCategory} className="mt-8">
          {/* 套餐卡片网格 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 items-start">
            {displayPlans.map((plan) => (
              <TenantPricingCard
                key={plan.code}
                plan={plan}
                onSelect={handleSelectPlan}
                highlighted={
                  plan.featured || plan.tier?.toLowerCase() === "business"
                }
                hasSubscription={hasSubscription}
              />
            ))}
          </div>

          {/* 空状态 */}
          {displayPlans.length === 0 && (
            <div className="text-center py-12 text-subtle">
              {t("noPlansAvailable")}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* 底部说明 */}
      <div className="text-center text-body text-subtle space-y-2">
        <p>{t("trialNote")}</p>
        <p>
          {t("needMoreSeats")}
          <Link
            href={`/${locale}/contact`}
            className="text-primary hover:underline ml-1"
          >
            {t("contactSales")}
          </Link>
        </p>
      </div>

      {/* 试用申请对话框 */}
      {selectedPlan && (
        <TrialApplicationDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          planCode={selectedPlan.code}
          planTier={selectedPlan.tier || ""}
          planName={selectedPlan.tier || selectedPlan.code}
          applicationType={getApplicationType(selectedPlan.tier || "")}
          applicationId={verifiedApplicationId}
          onSuccess={() => {
            setDialogOpen(false);
            setSelectedPlan(null);
            setVerifiedApplicationId(undefined);
          }}
        />
      )}
    </div>
  );
}
